import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../../assets/styles/colors.css";

const STATUS_BADGES = {
  built: { label: "Approved & Built", color: "text-green-600 dark:text-green-400 bg-green-500/10 border-green-500/30" },
  cancelled: { label: "Cancelled", color: "text-red-500 dark:text-red-400 bg-red-500/10 border-red-500/30" },
  approved: { label: "Approved", color: "text-green-600 dark:text-green-400 bg-green-500/10 border-green-500/30" },
  drafting: { label: "Drafting", color: "text-yellow-600 dark:text-yellow-400 bg-yellow-500/10 border-yellow-500/30" },
};

const CREATED_BY_LABELS = {
  draft: "Draft",
  revise: "Revised",
};

function PlanViewModal({
  show,
  planMd,
  planStatus,
  onClose,
  loading,
  onPlanAction,
  planActionLoading,
  currentPlanStatus,
  // Version navigation
  versions,
  currentVersion,
  totalVersions,
  onVersionChange,
  versionLoading,
}) {
  const { t } = useTranslation();

  if (!show) return null;

  const isPending = planStatus === "pending_approval" && currentPlanStatus === "pending_approval";
  const badge = !isPending && planStatus ? STATUS_BADGES[planStatus] || null : null;

  const hasVersions = totalVersions > 0;
  const currentMeta = hasVersions && versions
    ? versions.find((v) => v.version === currentVersion)
    : null;
  const createdByLabel = currentMeta
    ? CREATED_BY_LABELS[currentMeta.created_by] || currentMeta.created_by
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col rounded-xl border border-[var(--color-border)] bg-white dark:bg-[var(--color-bg)] shadow-lg dark:shadow-[0_0_20px_var(--color-shadow-primary)]">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-[var(--color-border)] px-6 py-4 bg-slate-50 dark:bg-[var(--color-bg)]">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-[var(--color-text-white)]">
              {t("Plan")}
            </h2>
            {badge && (
              <span className={`px-2 py-0.5 rounded-md border text-xs font-medium ${badge.color}`}>
                {t(badge.label)}
              </span>
            )}
            {isPending && (
              <span className="px-2 py-0.5 rounded-md border text-xs font-medium text-[var(--color-primary)] bg-[var(--color-primary)]/10 border-[var(--color-primary)]/30">
                {t("Pending Approval")}
              </span>
            )}
            {createdByLabel && (
              <span className="px-2 py-0.5 rounded-md border text-xs font-medium text-slate-500 dark:text-gray-400 bg-slate-100 dark:bg-gray-800 border-slate-300 dark:border-gray-600">
                {createdByLabel}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Version navigator */}
        {hasVersions && totalVersions > 1 && (
          <div className="flex items-center justify-center gap-3 px-6 py-2 border-b border-slate-200 dark:border-[var(--color-border)] bg-slate-50/50 dark:bg-[var(--color-bg)]">
            <button
              type="button"
              onClick={() => onVersionChange(currentVersion - 1)}
              disabled={currentVersion <= 1 || versionLoading}
              className="p-1 rounded text-slate-500 dark:text-gray-400 hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-xs text-slate-600 dark:text-gray-300 font-medium min-w-[80px] text-center">
              {versionLoading ? "..." : currentVersion + " / " + totalVersions}
            </span>
            <button
              type="button"
              onClick={() => onVersionChange(currentVersion + 1)}
              disabled={currentVersion >= totalVersions || versionLoading}
              className="p-1 rounded text-slate-500 dark:text-gray-400 hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading || versionLoading ? (
            <div className="flex items-center justify-center py-12">
              <svg className="w-6 h-6 animate-spin text-[var(--color-primary)]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="ml-2 text-sm text-slate-500 dark:text-gray-400">
                {t("Loading plan...")}
              </span>
            </div>
          ) : planMd ? (
            <div className="prose prose-sm dark:prose-invert max-w-none text-slate-800 dark:text-gray-200">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {planMd}
              </ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm text-slate-500 dark:text-gray-400 text-center py-8">
              {t("No plan available at this point")}
            </p>
          )}
        </div>

        {isPending && onPlanAction && planMd && (!hasVersions || currentVersion === totalVersions) && (
          <div className="flex items-center justify-end gap-3 border-t border-slate-200 dark:border-[var(--color-border)] px-6 py-4 bg-slate-50 dark:bg-[var(--color-bg)]">
            <button
              type="button"
              onClick={() => { onPlanAction("cancel"); onClose(); }}
              disabled={planActionLoading}
              className="px-4 py-2 rounded-lg border border-red-400/50 text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={() => { onPlanAction("revise"); onClose(); }}
              disabled={planActionLoading}
              className="px-4 py-2 rounded-lg border border-yellow-500/50 text-sm font-medium text-yellow-600 dark:text-yellow-400 hover:bg-yellow-500/10 transition-colors disabled:opacity-50"
            >
              {t("Revise")}
            </button>
            <button
              type="button"
              onClick={() => { onPlanAction("approve"); onClose(); }}
              disabled={planActionLoading}
              className="px-4 py-2 rounded-lg border border-green-500/50 text-sm font-medium text-green-600 dark:text-green-400 hover:bg-green-500/10 transition-colors disabled:opacity-50"
            >
              {planActionLoading ? (
                <span className="inline-flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t("Building...")}
                </span>
              ) : t("Approve")}
            </button>
          </div>
        )}
        {isPending && hasVersions && currentVersion < totalVersions && planMd && (
          <div className="flex items-center justify-center border-t border-slate-200 dark:border-[var(--color-border)] px-6 py-3 bg-slate-50 dark:bg-[var(--color-bg)]">
            <p className="text-xs text-slate-500 dark:text-gray-400">
              {t("Viewing an older version. Navigate to the latest version to approve, revise, or cancel.")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

PlanViewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  planMd: PropTypes.string,
  planStatus: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onPlanAction: PropTypes.func,
  planActionLoading: PropTypes.bool,
  currentPlanStatus: PropTypes.string,
  versions: PropTypes.arrayOf(
    PropTypes.shape({
      version: PropTypes.number,
      plan_status: PropTypes.string,
      created_by: PropTypes.string,
    }),
  ),
  currentVersion: PropTypes.number,
  totalVersions: PropTypes.number,
  onVersionChange: PropTypes.func,
  versionLoading: PropTypes.bool,
};

export default PlanViewModal;
